import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import union from 'lodash/union'
import plur from 'plur'
import * as React from 'react'
import {useContext, useEffect, useMemo, useReducer} from 'react'
import {shallowEqual} from 'react-redux'

import {BuildSelectionIdContext} from '../../../../contexts/buildSelection'
import {useLocator} from '../../../../contexts/locator'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getBuildsData, getBuildsLocatorWithPage} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import {buildSelections} from '../../../../slices/buildSelections'
import type {BuildId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {truthy} from '../../../../utils/guards'
import {getPoolCounters} from '../../../App/SidebarPanel/SidebarPanelContent/QueueSidebar/QueueSidebar.selectors'
import {BuildBatchActions} from '../../../common/Builds/BuildBatchActions/BuildBatchActions'
import {useHasAvailableActions} from '../../../common/Builds/Builds.hooks'
import IconButton from '../../../common/IconButton/IconButton'
import {usePage} from '../../../common/Pager/Pager.hooks'
import Plugin from '../../../common/Plugin/Plugin'
import PopupMenu from '../../../common/Popup/PopupMenu'

import styles from './QueueActions.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type ActionabilityDetectorProps = {
  buildId: BuildId
  markBuildIdAsActionable: (buildId: BuildId) => void
}
function ActionabilityDetector({buildId, markBuildIdAsActionable}: ActionabilityDetectorProps) {
  const hasAvailableActions = useHasAvailableActions(buildId)
  useEffect(() => {
    if (hasAvailableActions) {
      markBuildIdAsActionable(buildId)
    }
  }, [hasAvailableActions, markBuildIdAsActionable, buildId])
  return null
}

export function QueueActions() {
  const dispatch = useAppDispatch()
  const locator = useLocator() ?? ''
  const selectionId = useContext(BuildSelectionIdContext)
  const selection = useAppSelector(state => state.buildSelections[selectionId]) ?? []
  const poolCounters = useAppSelector(getPoolCounters)
  const [fetchAllBuilds, {allBuilds}] = restApi.endpoints.getAllQueuedBuilds.useLazyQuery({
    selectFromResult: ({data}) => ({
      allBuilds: data?.build ?? emptyArray,
    }),
  })
  React.useEffect(() => {
    fetchAllBuilds({locator: `${locator},count:10000,lookupLimit:10000`, fields: 'build(id)'})
  }, [poolCounters, locator, fetchAllBuilds])
  const allBuildIds = useMemo(() => allBuilds.map(build => build.id), [allBuilds])
  const [allActionableBuildIds, markBuildIdAsActionable] = useReducer(
    (state: BuildId[], action: BuildId) => (state.includes(action) ? state : [...state, action]),
    [],
  )
  const hasSelection = selection.length > 0
  const page = usePage()
  const actionableBuildIdsOnPage = useAppSelector(
    state =>
      getBuildsData(state, getBuildsLocatorWithPage(state, locator, page)).data.filter(id =>
        allActionableBuildIds.includes(id),
      ),
    shallowEqual,
  )
  // reset selector on filter change
  React.useEffect(() => {
    dispatch(buildSelections.actions.setSelection({selectionId, selection: emptyArray}))
  }, [dispatch, locator, selectionId])
  return (
    <div className={styles.actions}>
      {allBuildIds.map(buildId => (
        <ActionabilityDetector
          key={buildId}
          buildId={buildId}
          markBuildIdAsActionable={markBuildIdAsActionable}
        />
      ))}
      {allActionableBuildIds.length > 1 && (
        <Dropdown anchor={<Button dropdown>{'Select'}</Button>}>
          <PopupMenu
            closeOnSelect
            data={[
              actionableBuildIdsOnPage.length > 0 &&
                actionableBuildIdsOnPage.length < allActionableBuildIds.length && {
                  label: `${actionableBuildIdsOnPage.length} ${plur(
                    'build',
                    actionableBuildIdsOnPage.length,
                  )} on this page`,
                  onClick: () =>
                    dispatch(
                      buildSelections.actions.setSelection({
                        selectionId,
                        selection: union(actionableBuildIdsOnPage, selection),
                      }),
                    ),
                },
              {
                label: `All ${allActionableBuildIds.length} ${plur(
                  'build',
                  allActionableBuildIds.length,
                )}`,
                onClick: () =>
                  dispatch(
                    buildSelections.actions.setSelection({
                      selectionId,
                      selection: allActionableBuildIds,
                    }),
                  ),
              },
            ].filter(truthy)}
          />
        </Dropdown>
      )}
      {hasSelection && (
        <span>
          {`${selection.length} selected`}
          <IconButton
            className={styles.clear}
            icon="close"
            title="Clear"
            onClick={() =>
              dispatch(buildSelections.actions.setSelection({selectionId, selection: emptyArray}))
            }
          />
        </span>
      )}
      <BuildBatchActions />
      <Plugin className={styles.plugins} placeId={PlaceIdList.SAKURA_QUEUE_ACTIONS} />
    </div>
  )
}
